import { Image as ImageIcon } from "lucide-react";
import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { ProductCreativeHero } from "@/components/flowly/ProductCreativeHero/ProductCreativeHero";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { ImageLightbox } from "@/components/flowly/Lightbox/ImageLightbox/ImageLightbox";
import { ProductCreativeStylePicker } from "@/components/flowly/ProductCreativeStylePicker/ProductCreativeStylePicker";
import { FlowReadyCta } from "@/components/flowly/FlowReadyCta/FlowReadyCta";
import { PRODUCT_CREATIVE_MODEL_ID } from "@/config/productCreative";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { downloadMedia } from "@/lib/mediaUtils";
import { PRODUCT_CREATIVE_CONFIG_ANCHOR_ID } from "./constants";
import { useProductCreative } from "./useProductCreative";

export default function ProductCreativePage() {
  const {
    uploads,
    imageCount,
    aspectRatio,
    resolution,
    isUploading,
    isGenerating,
    results,
    lastShowcase,
    creativeMode,
    customDirection,
    availableAspectRatios,
    availableResolutions,
    dbImages,
    dbPendingImages,
    pendingCount,
    imageCompletingIds,
    failedImages,
    selectedMedia,
    handleAddFiles,
    handleRemoveUpload,
    handleCreativeModeChange,
    handleCustomDirectionChange,
    setImageCount,
    setAspectRatio,
    setResolution,
    handleGenerate,
    handleClearFailed,
    handleOpenLightbox,
    handleCloseLightbox,
    handleDownload,
    handleShare,
    handleDelete,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    canGenerate,
    t,
  } = useProductCreative();

  const hasUploads = uploads.length > 0;

  // Canonical pricing — same credit_costs row the server charges from.
  const { getImageGenerateCost } = useCreditCosts();
  const creditCost =
    getImageGenerateCost(resolution, PRODUCT_CREATIVE_MODEL_ID) * imageCount;

  return (
    <StudioPageLayout
      activeTab="marketing"
      activeApp="productCreative"
      footer={
        !selectedMedia ? (
          <GenerationBar
            onGenerate={handleGenerate}
            generating={isGenerating}
            disabled={!canGenerate}
            creditCost={creditCost}
            buttonLabel={t.productCreative.generateBtn}
            mediaType="image"
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
            availableAspectRatios={availableAspectRatios}
            resolution={resolution}
            onResolutionChange={setResolution}
            availableImageResolutions={availableResolutions}
            quantity={imageCount}
            onQuantityChange={setImageCount}
          />
        ) : undefined
      }
      lightbox={
        selectedMedia ? (
          <ImageLightbox
            imageUrl={selectedMedia.image_url ?? selectedMedia.displayUrl}
            imageId={selectedMedia.id}
            referenceImageUrl={selectedMedia.reference_image_url ?? undefined}
            createdAt={selectedMedia.created_at}
            resolution={selectedMedia.resolution}
            aspectRatio={selectedMedia.aspect_ratio}
            pipeline={selectedMedia.pipeline}
            prompt={selectedMedia.prompt}
            model={selectedMedia.model}
            onClose={handleCloseLightbox}
            onRegenerate={() => {
              handleCloseLightbox();
              handleGenerate();
            }}
            onDownload={handleDownload}
            onShare={handleShare}
            onDelete={handleDelete}
            navigation={
              dbImages.length > 1
                ? {
                    currentIndex: currentMediaIndex,
                    totalItems: dbImages.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
    >
      <div className="space-y-6 md:space-y-8 mt-2 md:mt-4">
        <ProductCreativeHero
          uploads={uploads}
          results={results}
          lastShowcase={lastShowcase}
          isUploading={isUploading}
          canGenerate={canGenerate}
          onAddFiles={handleAddFiles}
          onRemove={handleRemoveUpload}
          onGenerate={handleGenerate}
        />

        {hasUploads && (
          <div
            id={PRODUCT_CREATIVE_CONFIG_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <ProductCreativeStylePicker
              mode={creativeMode}
              customDirection={customDirection}
              onModeChange={handleCreativeModeChange}
              onCustomDirectionChange={handleCustomDirectionChange}
            />
          </div>
        )}

        {/* Inline "all set" CTA — same generate action as the footer bar.
            Mounted through the generation too so it doesn't flicker. */}
        {(canGenerate || isGenerating) && (
          <FlowReadyCta
            onGenerate={handleGenerate}
            generating={isGenerating}
            disabled={!canGenerate}
            creditCost={creditCost}
            buttonLabel={t.productCreative.generateBtn}
          />
        )}

        <CreationsGallery
          title={t.productCreative.results}
          pendingCount={pendingCount}
          pendingLabel={t.generate.processing}
          pendingItems={dbPendingImages.map((img) => ({ id: img.id, createdAt: img.created_at }))}
          pendingMediaType="image"
          completingIds={imageCompletingIds}
          failedItems={failedImages}
          failedLabel={t.generate.failedRefunded}
          onClearFailed={handleClearFailed}
          className="mt-2 mb-4"
          emptyIcon={ImageIcon}
          emptyTitle={t.productCreative.noResults}
        >
          {dbImages.map((img, idx) => {
            const url = img.image_url ?? img.displayUrl;
            return (
              <MediaGridItem
                key={img.id}
                imageUrl={img.displayUrl}
                hoverText={t.generate.clickToOpen}
                alt={`${t.productCreative.results} ${idx + 1}`}
                onClick={() => handleOpenLightbox(img)}
                onDownload={
                  url ? () => downloadMedia(url, "product-creative", "png") : undefined
                }
                downloadLabel={t.productCreative.downloadAria}
              />
            );
          })}
        </CreationsGallery>
      </div>
    </StudioPageLayout>
  );
}
